import type { HTMLAttributes } from 'react';
import { clsx } from '../clsx';
import styles from './Avatar.module.css';

export type AvatarSize = 26 | 28 | 32 | 36 | 40;
export type AvatarTone = 'navy' | 'sand' | 'quiet' | 'accent';

export interface AvatarProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Two letters, e.g. "JW". */
  initials: string;
  /** Full name for assistive technology; the initials alone are not announced. */
  name?: string | undefined;
  /** 26 px is the topbar avatar, 28 px the list avatar, 40 px the contact card. */
  size?: AvatarSize | undefined;
  /** `navy` (topbar, own user), `sand` (contact lists), `quiet` (task assignee, invited user), `accent`. */
  tone?: AvatarTone | undefined;
  /** Photo instead of initials; the initials remain the fallback while it loads. */
  src?: string | undefined;
}

/** Initials circle of the screens; `src` turns it into a photo with the same frame. */
export function Avatar({ initials, name, size = 26, tone = 'navy', src, className, ...rest }: AvatarProps) {
  const label = name ?? initials;
  return (
    <span
      role="img"
      aria-label={label}
      className={clsx(styles.avatar, styles[tone], styles[`s${size}`], className)}
      {...rest}
    >
      <span aria-hidden="true" className={styles.initials}>
        {initials}
      </span>
      {src ? <img className={styles.image} src={src} alt="" /> : null}
    </span>
  );
}
